'use client'

import { useSyncExternalStore } from 'react'
import { Moon, Sun } from 'lucide-react'
import { useI18n } from '@/components/I18nProvider'

type ThemeChoice = 'light' | 'dark'

interface PublicThemeSelectorProps {
  className?: string
}

const THEME_STORAGE_KEY = 'theme'
const themeListeners = new Set<() => void>()

const getStoredTheme = (): ThemeChoice => {
  if (typeof window === 'undefined') return 'light'
  return window.localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light'
}

const emitThemeChange = () => {
  themeListeners.forEach((listener) => listener())
}

const subscribeToTheme = (listener: () => void) => {
  themeListeners.add(listener)

  if (typeof window !== 'undefined') {
    const onStorage = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY) {
        listener()
      }
    }
    window.addEventListener('storage', onStorage)
    return () => {
      themeListeners.delete(listener)
      window.removeEventListener('storage', onStorage)
    }
  }

  return () => {
    themeListeners.delete(listener)
  }
}

export function PublicThemeSelector({ className = '' }: PublicThemeSelectorProps) {
  const selectedTheme = useSyncExternalStore(subscribeToTheme, getStoredTheme, () => 'light')
  const { t } = useI18n()

  const saveThemePreference = (theme: ThemeChoice) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme)
      emitThemeChange()
    }
  }

  return (
    <div className={`inline-flex items-center gap-1 rounded-2xl border border-[#bfece5] bg-white/78 p-1 ${className}`}>
      <button
        type="button"
        onClick={() => saveThemePreference('light')}
        className={`inline-flex items-center gap-1 rounded-xl px-2.5 py-1 text-xs font-semibold transition ${
          selectedTheme === 'light'
            ? 'bg-[linear-gradient(135deg,#14b8a6,#0f766e)] text-white'
            : 'text-ink hover:bg-[#ecfeff]'
        }`}
        aria-label={t({ es: 'Tema claro para la app', en: 'Light theme for app' })}
      >
        <Sun className="h-3.5 w-3.5" suppressHydrationWarning />
        {t({ es: 'Claro', en: 'Light' })}
      </button>
      <button
        type="button"
        onClick={() => saveThemePreference('dark')}
        className={`inline-flex items-center gap-1 rounded-xl px-2.5 py-1 text-xs font-semibold transition ${
          selectedTheme === 'dark'
            ? 'bg-[linear-gradient(135deg,#0f172a,#134e4a)] text-white'
            : 'text-ink hover:bg-[#ecfeff]'
        }`}
        aria-label={t({ es: 'Tema oscuro para la app', en: 'Dark theme for app' })}
      >
        <Moon className="h-3.5 w-3.5" suppressHydrationWarning />
        {t({ es: 'Oscuro', en: 'Dark' })}
      </button>
    </div>
  )
}
